import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, ArrowLeft, Lock } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/admin/dashboard");
    } catch (e) {
      const m = formatApiError(e.response?.data?.detail) || "Login failed";
      setErr(m);
      toast.error(m);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#151816] flex flex-col" data-testid="admin-login-page">
      <header className="px-6 py-5 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2 text-white" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono-label text-[10px]">Back to site</span>
        </Link>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#5BB239] flex items-center justify-center">
            <Truck className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-extrabold tracking-tight text-white text-sm">
            ThemBoys<span className="text-[#5BB239]">.</span>
          </span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-5">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-[#5BB239] mb-6">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <div className="overline text-[#5BB239]">Admin Access</div>
            <h1 className="mt-3 font-display font-black text-4xl text-white tracking-tight">
              Dispatch Console
            </h1>
            <p className="mt-3 text-white/60 text-sm">
              Sign in to manage bookings and customer requests.
            </p>
          </div>

          <form onSubmit={submit} className="bg-[#F5F3EC] p-8 space-y-5">
            <div>
              <Label className="font-mono-label text-[10px] text-[#5C635F]">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="admin@themboysmoving.com"
                className="mt-2 rounded-none border-[#DDD8C9] h-12"
                data-testid={ADMIN.emailInput}
              />
            </div>
            <div>
              <Label className="font-mono-label text-[10px] text-[#5C635F]">Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 rounded-none border-[#DDD8C9] h-12"
                data-testid={ADMIN.passwordInput}
              />
            </div>
            {err && (
              <div
                role="alert"
                className="bg-red-50 border-l-2 border-red-500 px-3 py-2 text-sm text-red-700"
                data-testid="login-error"
              >
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#5BB239] text-white py-4 font-bold uppercase tracking-wider text-sm hover:bg-[#499828] transition-colors disabled:opacity-60"
              data-testid={ADMIN.loginBtn}
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
